import { getTimeFromDate } from 'utils/date';
import { lotSize } from 'global/TradingModal/constant';
import numeral from 'numeral';
import { runningTradeAction } from 'constants/datafeed';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import parseDate from 'date-fns/parse';
import compareAsc from 'date-fns/compareAsc';
import { TableProps } from 'antd';
import Link from 'next/link';
import RunningTradeEmpty from './RunningTradeEmpty';
import { Skeleton, Table, Td, TdAction, TdPrice, TdSymbol, Th } from './styled';

type RunningTradeTableProps = {
  initialData?: GetInitRunningTradeItem[];
  data: any;
  loading: boolean;
  showAction?: boolean;
  skeletonRows?: number;
  minDataLength?: number;
  height?: CSSProperties['height'];
  hideCode?: boolean;
  isPaused?: boolean;
  isCodeBold?: boolean;
  isVolumeColor?: boolean;
};

type OnChange = NonNullable<TableProps<any>['onChange']>;

type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

function RunningTradeTable({
  initialData,
  data,
  loading,
  showAction = false,
  skeletonRows = 20,
  minDataLength = 10,
  height,
  hideCode = false,
  isPaused = false,
  isCodeBold = false,
  isVolumeColor = false,
}: RunningTradeTableProps) {
  const [sortedInfo, setSortedInfo] = useState<Sorts>({});

  useEffect(() => {
    if (isPaused) {
      setSortedInfo({});
    }
  }, [isPaused]);

  const handleChange: OnChange = (___pagination, ___filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const formatInitialData = (initialDataSource: GetInitRunningTradeItem[]) => {
    if (!initialDataSource) return [];

    const initialDataSliced =
      initialDataSource.length > minDataLength
        ? initialDataSource.slice(0, minDataLength)
        : initialDataSource;

    return initialDataSliced.map(
      ({ action, change, code, price, time, lot }, i) => ({
        key: `${i}`,
        action: Object.keys(runningTradeAction).find(
          (key) =>
            runningTradeAction[key] ===
            action.charAt(0).toUpperCase() + action.slice(1),
        ),
        change: {
          value: change || 0,
          percentage: change ? parseFloat(change).toFixed(2) : 0,
        },
        time,
        code,
        price: `${price.toLocaleString()} (${change})`,
        volume: lot,
      }),
    );
  };

  const dataSource = data.map(
    ({ time, change, stock, price, action, volume }, i) => ({
      key: `${i}`,
      action,
      change: {
        value: change?.value || 0,
        percentage: change?.percentage || 0,
      },
      time: getTimeFromDate(new Date(Number(time.seconds) * 1000)),
      code: stock,
      price: `${price.toLocaleString()} (${
        change && change.percentage > 0 ? '+' : ''
      }${change ? `${numeral(change.percentage).format('0.00')}%` : '0.00'})`,
      volume: (volume / lotSize).toLocaleString(),
    }),
  );

  const columnsWidth = useMemo(() => {
    if (hideCode) {
      return ['20%', '0%', showAction ? 'auto' : '20%', '20%', '20%'];
    }
    return ['20%', '20%', showAction ? 'auto' : '20%', '13%', '18%'];
  }, [hideCode]);

  const columns = [
    {
      title: <Th>Time</Th>,
      width: columnsWidth[0],
      dataIndex: 'time',
      key: 'time',
      render: (time, record) => {
        const { action, change } = record;

        return (
          <Td type={action} change={change.percentage} isColored={false}>
            {time}
          </Td>
        );
      },
      showSorterTooltip: false,
      sorter: (a, b) => {
        if (isPaused) {
          const timeA = parseDate(a.time, 'HH:mm:ss', new Date());
          const timeB = parseDate(b.time, 'HH:mm:ss', new Date());

          return compareAsc(timeA, timeB);
        }
        return false;
      },
      sortOrder: sortedInfo.columnKey === 'time' && sortedInfo?.order,
      sortDirections: isPaused ? ['ascend', 'descend'] : [],
    },
    ...(!hideCode
      ? [
          {
            title: <Th>Code</Th>,
            width: columnsWidth[1],
            dataIndex: 'code',
            key: 'code',
            render: (code) => (
              <TdSymbol>
                {isPaused ? (
                  <Link href={`/symbol/${code}`}>
                    <a target="_blank">{code}</a>
                  </Link>
                ) : (
                  code
                )}
              </TdSymbol>
            ),
          },
        ]
      : []),
    {
      title: <Th>Price</Th>,
      width: columnsWidth[2],
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => {
        const { change, action } = record;

        return (
          <TdPrice type={action} change={change.percentage} isColored>
            {price}
          </TdPrice>
        );
      },
    },
    ...(showAction
      ? [
          {
            title: <Th>Action</Th>,
            width: columnsWidth[3],
            dataIndex: 'action',
            key: 'action',
            render: (action: RunningTradeStockAction, record) => {
              const { change } = record;

              if (!action) {
                return <Td />;
              }

              return (
                <TdAction type={action} change={change.percentage} isColored>
                  {runningTradeAction[action]}
                </TdAction>
              );
            },
          },
        ]
      : []),
    {
      title: <Th>Lot</Th>,
      width: columnsWidth[4],
      dataIndex: 'volume',
      key: 'volume',
      render: (volume, record) => {
        const { action } = record;
        return (
          <Td type={action} isColored={isVolumeColor}>
            {volume}
          </Td>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Skeleton
        paragraph={{
          width: '100%',
          rows: skeletonRows,
        }}
      />
    );
  }

  return (
    <Table
      tableLayout="fixed"
      style={{ overflow: 'hidden' }}
      locale={{
        emptyText: <RunningTradeEmpty />,
      }}
      dataSource={
        dataSource.length < minDataLength && initialData
          ? dataSource.concat(
              formatInitialData(initialData).slice(
                dataSource.length,
                minDataLength,
              ),
            )
          : dataSource
      }
      columns={columns}
      pagination={false}
      borderless
      scroll={{
        y: data.length > 0 ? height : undefined,
      }}
      onChange={handleChange}
    />
  );
}

export default RunningTradeTable;
